import React, { useRef, useLayoutEffect, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, Stack } from '@mui/material';
import { Chip, Theme, Tooltip } from '@routific/hive';

interface Props {
  tags: string[];
  /**
   * When provided, use this element's width for overflow calculation instead of the internal
   * container. Use the table cell (td) ref to measure against the column's final width.
   */
  widthSourceRef?: React.RefObject<HTMLElement | null>;
}

const stackSx = {
  position: 'relative' as const,
  minWidth: 0,
  display: 'flex',
  flexWrap: 'nowrap' as const,
  alignItems: 'center',
  gap: Theme.Spacing.xsSpacing,
  overflow: 'hidden',
};

const measureContainerSx = {
  ...stackSx,
  visibility: 'hidden' as const,
  pointerEvents: 'none' as const,
  position: 'fixed' as const,
  top: -9999,
  left: 0,
};

const tagChipWrapperSx = {
  minWidth: 0,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
};

const overflowChipSx = {
  cursor: 'default',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
};

const TagsOverflowRow: React.FC<Props> = ({ tags, widthSourceRef }) => {
  // Retry ~1s (60 frames) for the width source to mount after a layout shift.
  const maxObserveAttempts = 60;
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tags.length);

  const recalculate = useCallback(() => {
    const measureContainer = measureRef.current;
    const widthSource = widthSourceRef?.current ?? containerRef.current;
    if (!widthSource || !measureContainer) return;

    const containerWidth = widthSource.getBoundingClientRect().width;
    const children = Array.from(measureContainer.children) as HTMLElement[];
    if (children.length === 0) return;

    const gap = 4;
    // Hive small Chip (+N) width; revisit if Chip sizing changes.
    const overflowChipWidth = 36;
    let usedWidth = 0;
    let count = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].getBoundingClientRect().width;
      const nextWidth = usedWidth + (count > 0 ? gap : 0) + childWidth;

      const isLast = i === children.length - 1;
      const spaceNeededWithOverflow = nextWidth + gap + overflowChipWidth;

      if (isLast && nextWidth <= containerWidth) {
        count++;
      } else if (!isLast && spaceNeededWithOverflow > containerWidth) {
        break;
      } else if (nextWidth > containerWidth) {
        break;
      } else {
        usedWidth = nextWidth;
        count++;
      }
    }

    setVisibleCount(Math.max(1, count));
  }, [tags, widthSourceRef]);

  useLayoutEffect(() => {
    recalculate();
  }, [recalculate]);

  useEffect(() => {
    let observer: ResizeObserver | null = null;
    let rafId: number | null = null;
    let attempts = 0;

    const observeWidthSource = () => {
      const elementToObserve = widthSourceRef?.current ?? containerRef.current;
      if (!elementToObserve) {
        if (attempts >= maxObserveAttempts) {
          return;
        }

        attempts += 1;
        rafId = requestAnimationFrame(observeWidthSource);
        return;
      }

      observer = new ResizeObserver(() => {
        recalculate();
      });
      observer.observe(elementToObserve);
      recalculate();
    };

    observeWidthSource();

    return () => {
      observer?.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [recalculate, widthSourceRef]);

  if (!tags.length) return null;

  const visibleTags = tags.slice(0, visibleCount);
  const overflowTags = tags.slice(visibleCount);
  const hasOverflow = overflowTags.length > 0;

  const measureContent = (
    <Stack ref={measureRef} direction="row" sx={measureContainerSx} aria-hidden="true">
      {tags.map((tag) => (
        <Chip key={tag} label={tag} size="small" />
      ))}
    </Stack>
  );

  return (
    <>
      {createPortal(measureContent, document.body)}
      <Stack ref={containerRef} direction="row" sx={stackSx} data-testid="route-tags-row">
        {visibleTags.map((tag) => (
          <Box key={tag} sx={tagChipWrapperSx}>
            <Chip
              label={tag}
              size="small"
              title=""
              data-testid={`route-tag-chip-${tag}`}
            />
          </Box>
        ))}
        {hasOverflow && (
          <Tooltip
            title={(
              <Box component="ul" sx={{ margin: 0, paddingLeft: 2, '& li': { wordBreak: 'break-word' } }}>
                {overflowTags.map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
              </Box>
            )}
            placement="bottom"
          >
            <Box sx={overflowChipSx}>
              <Chip
                label={`+${overflowTags.length}`}
                size="small"
                data-testid="route-tags-overflow"
              />
            </Box>
          </Tooltip>
        )}
      </Stack>
    </>
  );
};

export default TagsOverflowRow;
